import { isValidElement, type ButtonHTMLAttributes, type ComponentType, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

// ── Button ────────────────────────────────────────────────────────────────────
//
// Sdílené tlačítko pro celý ACM Dashboard.
//
// variant:
//   primary   — brand plex (hlavní akce, barva ze schématu)
//   secondary — šedá (vedlejší akce, zrušit, zpět)
//   success   — zelená (start, nasadit)
//   warning   — žlutá (restart, pauza)
//   danger    — červená (stop, smazat)
//   ghost     — průhledné (ikony, inline akce)
//
// size:
//   sm  — px-3 py-1.5 text-xs  (v kartách, v řádcích)
//   md  — px-4 py-2   text-sm  (modální formuláře, hlavní CTA)
//   icon — čtverec p-1.5 (jen ikona v hover skupině)

export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'icon';

const VARIANTS: Record<ButtonVariant, string> = {
  /* primary/secondary/ghost: hloubka z index.css (stejná „hmota“) */
  primary: 'acm-btn-primary',
  secondary: 'acm-btn-secondary',
  success:
    'acm-btn-tonal bg-emerald-600/22 hover:bg-emerald-600/32 text-emerald-200 border-emerald-500/40',
  warning:
    'acm-btn-tonal bg-amber-500/18 hover:bg-amber-500/28 text-amber-300 border-amber-500/40',
  danger:
    'acm-btn-tonal bg-red-500/18 hover:bg-red-500/28 text-red-300 border-red-500/40',
  ghost: 'acm-btn-ghost',
};

const SIZES: Record<ButtonSize, string> = {
  /* rounded-lg — méně „pilulek“ než xl; sjednoceno s poli/selecty (LHUI 2.1) */
  sm: 'px-3 py-1.5 text-xs rounded-lg gap-1.5',
  md: 'px-4 py-2   text-sm rounded-lg gap-2',
  icon: 'p-1.5       text-xs rounded-md  gap-0',
};

type IconComponent = ComponentType<{ className?: string; size?: number | string }>;

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * Hotový element (`<RefreshCw size={14} />`) NEBO komponenta (`RefreshCw`).
   * Komponenta se vykreslí s size podle Button size — dřív se bare komponenta
   * vložila jako child a spustila React #31 (object not valid as child).
   */
  icon?: ReactNode | IconComponent;
  children?: ReactNode;
}

function renderIcon(
  icon: ReactNode | IconComponent | undefined,
  size: ButtonSize,
): ReactNode {
  if (icon == null || icon === false) return null;
  if (isValidElement(icon)) return icon;
  // Lucide / forwardRef / function component — ne renderovat object jako child
  if (typeof icon === 'function' || (typeof icon === 'object' && icon !== null && '$$typeof' in (icon as object))) {
    const Icon = icon as IconComponent;
    const px = size === 'md' ? 14 : size === 'icon' ? 14 : 12;
    return <Icon size={px} />;
  }
  // string / number / etc.
  return icon as ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'sm',
  loading = false,
  icon,
  children,
  disabled,
  className,
  ...rest
}: ButtonProps) {
  const iconEl = renderIcon(icon, size);
  return (
    <button
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center font-medium border transition-all duration-150',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading
        ? <Loader2 size={size === 'md' ? 13 : 11} className='animate-spin flex-shrink-0' />
        : iconEl && <span className='flex-shrink-0'>{iconEl}</span>}
      {children}
    </button>
  );
}
